import React, { useState, useEffect } from 'react';

interface ExpenseCategory {
  id: string;
  value: string;
  parentId: string | null;
  type: string;
  code?: number;
}

interface TreeNode {
  id: string;
  value: string;
  parentId: string | null;
  type: string;
  code?: number;
  children: TreeNode[];
  isExpanded: boolean;
  isSelected: boolean;
}

interface ExpenseCategoryTreeProps {
  categories: ExpenseCategory[];
  selectedCategories: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  isDarkMode?: boolean;
}

export const ExpenseCategoryTree: React.FC<ExpenseCategoryTreeProps> = ({
  categories,
  selectedCategories,
  onSelectionChange,
  isDarkMode = false
}) => {
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');


  useEffect(() => {
    const buildTree = (items: ExpenseCategory[]): TreeNode[] => {
      const itemMap = new Map<string, TreeNode>();
      const roots: TreeNode[] = [];


      items.forEach(item => {
        itemMap.set(item.id, {
          ...item,
          children: [],
          isExpanded: true,
          isSelected: selectedCategories.includes(item.id)
        });
      });

      items.forEach(item => {
        const node = itemMap.get(item.id)!;
        if (item.parentId === null) {
          roots.push(node);
        } else {
          const parent = itemMap.get(item.parentId);
          if (parent) {
            parent.children.push(node);
          }
        }
      });

      return roots;
    };

    setTreeData(buildTree(categories));
  }, [categories, selectedCategories]);

  const handleNodeToggle = (nodeId: string) => {
    const toggleNode = (nodes: TreeNode[]): TreeNode[] => {
      return nodes.map(node => {
        if (node.id === nodeId) {
          return { ...node, isExpanded: !node.isExpanded };
        }
        if (node.children.length > 0) {
          return { ...node, children: toggleNode(node.children) };
        }
        return node;
      });
    };
    setTreeData(toggleNode(treeData));
  };

  const handleNodeSelect = (nodeId: string, isSelected: boolean) => {
    const updateSelection = (nodes: TreeNode[]): TreeNode[] => {
      return nodes.map(node => {
        if (node.id === nodeId) {
          return { ...node, isSelected };
        }
        if (node.children.length > 0) {
          return { ...node, children: updateSelection(node.children) };
        }
        return node;
      });
    };

    setTreeData(updateSelection(treeData));

    const newSelection = isSelected
      ? [...selectedCategories, nodeId]
      : selectedCategories.filter(id => id !== nodeId);
    
    onSelectionChange(newSelection);
  };

  const handleParentSelect = (nodeId: string, isSelected: boolean) => {
    const selectAllChildren = (nodes: TreeNode[]): TreeNode[] => {
      return nodes.map(node => {
        if (node.id === nodeId) {
          return { ...node, isSelected };
        }
        if (node.children.length > 0) {
          return { ...node, children: selectAllChildren(node.children) };
        }
        return node;
      });
    };

    setTreeData(selectAllChildren(treeData));

 
    const getAllChildrenIds = (nodes: TreeNode[], parentId: string): string[] => {
      let childrenIds: string[] = [];
      nodes.forEach(node => {
        if (node.parentId === parentId) {
          childrenIds.push(node.id);
          childrenIds = [...childrenIds, ...getAllChildrenIds(node.children, node.id)];
        }
        if (node.children.length > 0) {
          childrenIds = [...childrenIds, ...getAllChildrenIds(node.children, parentId)];
        }
      });
      return childrenIds;
    };

    const allChildrenIds = getAllChildrenIds(treeData, nodeId);
    
    let newSelection: string[];
    if (isSelected) {
      // Add only children that aren't already selected, and exclude the parent
      const newChildrenIds = allChildrenIds.filter(id => !selectedCategories.includes(id));
      newSelection = [...selectedCategories, ...newChildrenIds];
    } else {
      // Remove parent and all its children
      newSelection = selectedCategories.filter(id => id !== nodeId && !allChildrenIds.includes(id));
    }
    
    onSelectionChange(newSelection);
  };


  const handleClearAll = () => {
    onSelectionChange([]);
  };

  const renderTreeNode = (node: TreeNode, level: number = 0) => {
    const hasChildren = node.children.length > 0;
    const indentStyle = { paddingLeft: `${level * 20}px` };

    return (
      <div key={node.id} className="select-none">
        <div
          className={`flex items-center py-1 px-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer rounded ${
            node.isSelected ? 'bg-blue-100 dark:bg-blue-900' : ''
          }`}
          style={indentStyle}
        >
          {hasChildren && (
            <button
              onClick={() => handleNodeToggle(node.id)}
              className="mr-2 w-4 h-4 flex items-center justify-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              {node.isExpanded ? '▼' : '▶'}
            </button>
          )}
          {!hasChildren && <div className="w-4 h-4 mr-2" />}
          
          <input
            type="checkbox"
            checked={node.isSelected}
            onChange={(e) => {
              if (hasChildren) {
                handleParentSelect(node.id, e.target.checked);
              } else {
                handleNodeSelect(node.id, e.target.checked);
              }
            }}
            className="mr-2 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
          />
          
          <span className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} ${hasChildren ? 'font-semibold' : ''}`}>
            {node.value}
            {hasChildren && <span className="ml-1 text-xs text-gray-500">({node.children.length} items)</span>}
          </span>
        </div>
        
        {hasChildren && node.isExpanded && (
          <div>
            {node.children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const filteredTreeData = searchTerm 
    ? treeData.filter(node => 
        node.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
        node.children.some(child => child.value.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : treeData;

  return (
    <div className={`border rounded-lg p-3 max-h-96 overflow-y-auto ${
      isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'
    }`}>
      <div className="flex justify-between items-center mb-3">
        <h3 className={`font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
          Expense Categories
        </h3>
        <div className="flex gap-2">
          <button
            onClick={handleClearAll}
            className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Clear All
          </button>
        </div>
      </div>
      
      <div className="mb-3">
        <input
          type="text"
          placeholder="Search categories..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={`w-full p-2 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            isDarkMode 
              ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400' 
              : 'bg-white border-gray-300 text-gray-700'
          }`}
        />
      </div>
      
      <div className="space-y-1">
        {filteredTreeData.map(node => renderTreeNode(node))}
      </div>
      
      {selectedCategories.length > 0 && (
        <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-900 rounded">
          <p className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Selected: {selectedCategories.length} category(ies)
          </p>
        </div>
      )}
    </div>
  );
};
